"use client";

import {
    AccountDetailsCollection,
    AccountDetailsCollectionConfig,
    AccountDetailsCollectionName,
    AllCountries,
} from "@finbackoffice/fe-core";
import classNames from "classnames";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Controller, FieldError, useForm } from "react-hook-form";
import {
    ClientBFFContext,
    getCountries,
    getCountryStates,
    getGenders,
    useCountryStates,
    useProfileDetailsConfig,
    userInfoValidationSchema,
    useRuntimeConfig,
} from "@finbackoffice/site-core";
import { ConfigContextV2, UserAccountContextV2 } from "contexts";
import { InferType } from "yup";
import { IProfileResponse, IUpdateProfileRequest } from "@finbackoffice/clientbff-client";
import { yupResolver } from "@hookform/resolvers/yup";
import { Svg } from "components/base/svg/Svg";
import {
    AssetsDirectory,
    Country,
    SignupInputFieldName,
    TranslationScopes,
} from "@finbackoffice/enums";
import Translate from "components/base/translate/Translate";
import PhoneInput from "components/base/phone-input/PhoneInput";
import Input from "components/base/input-field/Input";
import AccountDetailsFormBlock from "components/account/details/form-block/AccountDetailsFormBlock";
import { DEFAULT_DATE_FORMAT, useDate, useNotification } from "hooks";
import DatePicker from "components/base/date-picker/DatePicker";
import VerificationItem from "components/account/verification/item/VerificationItem";
import Button from "components/base/button/Button";
import UniversalDeviceDropdown from "components/base/universal-device-dropdown/UniversalDeviceDropdown";
import { useTranslation } from "react-i18next";
import styles from "./user-info-form.module.sass";

type InfoForm = InferType<typeof userInfoValidationSchema>;

type IProps = {
    config: AccountDetailsCollection;
};

const UserInfoForm: FC<IProps> = ({ config }) => {
    const client = useContext(ClientBFFContext);
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { t } = useTranslation();

    const { isPhoneVerificationRequired, isEmailVerificationRequired } =
        useContext(ConfigContextV2);

    const { updateUser, userData, isEmailVerified, isPhoneVerified } =
        useContext(UserAccountContextV2);

    const { notification, setNotification } = useNotification();
    const { formatDate } = useDate();

    const [showVerificationDialog, setShowVerificationDialog] = useState(false);
    const [loading, setLoading] = useState(false);

    const accountDetailsConfigFieldsMemo = useMemo(() => {
        const result: (keyof InfoForm)[] = [];
        if (config) {
            switch (config.name) {
                case AccountDetailsCollectionName.Identity:
                case AccountDetailsCollectionName.Address:
                    config.config?.forEach((input) => {
                        result.push(input.name as keyof InfoForm);
                    });
                    break;
                default:
                    result.push(config.name as keyof InfoForm);
            }
        }

        return result;
    }, [config]);
    const { accountDetailsConfigObject } = useProfileDetailsConfig(accountDetailsConfigFieldsMemo);

    const getDefaultValues = useCallback(
        (userData: IProfileResponse | null) => {
            const defaultValues = {} as InfoForm;
            const key = config?.name as keyof InfoForm;
            if (config.config) {
                config.config?.forEach((input) => {
                    if (input.enable) {
                        defaultValues[input.name as keyof InfoForm] =
                            userData?.[input.name as keyof InfoForm] || "";
                    }
                });
            } else {
                defaultValues[key] = userData?.[key] || "";
            }

            return defaultValues;
        },
        [config.config, config?.name],
    );

    const defaultValuesMemo = useMemo(() => {
        return getDefaultValues(userData);
    }, [getDefaultValues, userData]);

    const {
        handleSubmit,
        setError,
        getValues,
        control,
        reset,
        clearErrors,
        watch,
        formState: { errors, isDirty, isSubmitting, isValid, dirtyFields },
    } = useForm({
        mode: "onChange",
        context: { configObj: { ...accountDetailsConfigObject, t } },
        resolver: yupResolver(
            userInfoValidationSchema.pick(accountDetailsConfigFieldsMemo).partial(),
        ),
        defaultValues: defaultValuesMemo,
    });
    const selectedCountry = watch("country");
    const { loading: countryStatesLoading, states } = useCountryStates(
        config.name === AccountDetailsCollectionName.Address &&
            !!config.config?.find((field) => field.name === SignupInputFieldName.State),
        selectedCountry as Country,
    );

    useEffect(() => {
        if (config?.name === "email" || config?.name === "phone") {
            if (isPhoneVerified && isEmailVerified && showVerificationDialog) {
                setTimeout(() => {
                    setShowVerificationDialog(false);
                }, 2000);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEmailVerified, isPhoneVerified]);

    const updateProfile = useCallback(
        async (req: IUpdateProfileRequest) => {
            setLoading(true);
            try {
                const response = await client.updateProfile(req);

                if (response) {
                    updateUser(response);
                    reset(getDefaultValues(response), { keepValues: true });
                    setLoading(false);
                }
            } catch (err: any) {
                const error = err.response?.data;

                switch (error.error) {
                    case "registration_error":
                        error.details?.conflicts.forEach((field: PropertyKey) => {
                            if (getValues().hasOwnProperty(field)) {
                                setError(field as keyof InfoForm, {
                                    type: "conflict",
                                    message: t("validation_err_alreadyExists", {
                                        field,
                                    }),
                                });
                            }
                        });
                        break;
                    case "validation_error":
                        if (error.details) {
                            Object.keys(error.details).forEach((field) => {
                                if (getValues().hasOwnProperty(field)) {
                                    setError(field as keyof InfoForm, {
                                        type: "validation",
                                        message: Object.values<string>(error.details[field])[0],
                                    });
                                }
                            });
                        }
                        break;
                    default:
                        if (error.error) {
                            setNotification({
                                type: "error",
                                error: error.error,
                                message: error.message,
                            });
                        }
                }

                setLoading(false);
            }
        },
        [client, getDefaultValues, getValues, reset, setError, setNotification, t, updateUser],
    );

    const onSubmit = useCallback(
        async (data: InfoForm) => {
            if (isDirty && !isSubmitting && isValid && !errors.root?.apiError && !loading) {
                let updateProfileRequestObj = {} as IUpdateProfileRequest;
                Object.keys(dirtyFields).forEach((field) => {
                    updateProfileRequestObj = {
                        ...updateProfileRequestObj,
                        [field]: data[field as keyof InfoForm],
                    };
                });

                await updateProfile(updateProfileRequestObj);
            }
        },
        [
            dirtyFields,
            errors.root?.apiError,
            isDirty,
            isSubmitting,
            isValid,
            loading,
            updateProfile,
        ],
    );

    const handleCancel = () => {
        reset(defaultValuesMemo);
        if (showVerificationDialog) {
            setShowVerificationDialog(false);
        }
    };

    const renderLabel = useCallback(
        (isOpened?: boolean) => {
            switch (config?.name) {
                case AccountDetailsCollectionName.Phone:
                    return (
                        <>
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-mobile.svg`}
                                className="svg-icon"
                            />
                            <span>
                                {isOpened ? <Translate tid="userData_mobile" /> : userData?.phone}
                            </span>
                            {!isOpened &&
                                isPhoneVerificationRequired &&
                                (isPhoneVerified ? (
                                    <Svg
                                        className={styles.iconVerified}
                                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-verified.svg`}
                                    />
                                ) : (
                                    <span className={styles.verifyBtn}>
                                        <span>
                                            <Translate tid="verification_verify" />
                                        </span>
                                    </span>
                                ))}
                        </>
                    );
                case AccountDetailsCollectionName.Email:
                    return (
                        <>
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-mail.svg`}
                                className="svg-icon"
                            />
                            <span>
                                {isOpened ? (
                                    <Translate tid="userData_email" />
                                ) : (
                                    userData?.email || "N/A"
                                )}
                            </span>
                            {!isOpened &&
                                isEmailVerificationRequired &&
                                (isEmailVerified ? (
                                    <Svg
                                        className={styles.iconVerified}
                                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-verified.svg`}
                                    />
                                ) : (
                                    <span className={styles.verifyBtn}>
                                        <span>
                                            <Translate tid="verification_verify" />
                                        </span>
                                    </span>
                                ))}
                        </>
                    );
                case AccountDetailsCollectionName.Identity:
                    return (
                        <>
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-name.svg`}
                                className="svg-icon"
                            />
                            <span>
                                {userData?.first_name || userData?.last_name
                                    ? `${userData.first_name} ${userData.last_name}`
                                    : "N/A"}
                            </span>
                        </>
                    );
                case AccountDetailsCollectionName.BirthDate:
                    return (
                        <>
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-birthdate.svg`}
                                className="svg-icon"
                            />
                            <span>
                                {isOpened ? (
                                    <Translate tid="userData_birthday" />
                                ) : userData?.birth_date ? (
                                    formatDate(
                                        new Date(userData.birth_date),
                                        DEFAULT_DATE_FORMAT,
                                        false,
                                    )
                                ) : (
                                    <Translate tid="userData_birthday" />
                                )}
                            </span>
                        </>
                    );

                case AccountDetailsCollectionName.Address:
                    return (
                        <>
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-address.svg`}
                                className="svg-icon"
                            />
                            <span>
                                {isOpened ? (
                                    <Translate tid="userData_address" />
                                ) : (
                                    <>
                                        {userData && (AllCountries[userData.country] || "")}
                                        {userData?.city ? `, ${userData.city}` : ""}
                                        {userData?.address ? `, ${userData.address}` : ""}
                                    </>
                                )}
                            </span>
                            {/* <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-warning.svg`}
                                 
                                className={classNames("svg-icon", styles.warningIcon)}
                            /> */}
                        </>
                    );
                case AccountDetailsCollectionName.BankDetails:
                    return (
                        <>
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-payment.svg`}
                                className="svg-icon"
                            />
                            <span>Bank Details</span>
                        </>
                    );
                default:
                    return null;
            }
        },
        [
            config?.name,
            formatDate,
            isEmailVerificationRequired,
            isEmailVerified,
            isPhoneVerificationRequired,
            isPhoneVerified,
            userData,
        ],
    );

    const renderForm = useMemo(() => {
        const renderIdentityFields = (field: AccountDetailsCollectionConfig) => {
            if (field.enable) {
                switch (field.name) {
                    case SignupInputFieldName.FirstName:
                        return (
                            <div className={styles.field} key={field.name}>
                                <Controller
                                    render={({ field: { onChange, value, name } }) => (
                                        <Input
                                            label={t("userData_firstName")}
                                            name={name}
                                            onChange={onChange}
                                            value={value}
                                            error={errors.first_name}
                                            disabled={!field.editable && !!value}
                                            required={field.required}
                                        />
                                    )}
                                    control={control}
                                    name="first_name"
                                />
                            </div>
                        );
                    case SignupInputFieldName.LastName:
                        return (
                            <div className={styles.field} key={field.name}>
                                <Controller
                                    render={({ field: { onChange, value, name } }) => (
                                        <Input
                                            label={t("userData_lastName")}
                                            name={name}
                                            onChange={onChange}
                                            value={value}
                                            error={errors.last_name}
                                            disabled={!field.editable && !!value}
                                            required={field.required}
                                        />
                                    )}
                                    control={control}
                                    name="last_name"
                                />
                            </div>
                        );
                    case SignupInputFieldName.Gender:
                        return (
                            <div className={styles.field} key={field.name}>
                                <Controller
                                    render={({ field: { onChange, value, name } }) => (
                                        <UniversalDeviceDropdown
                                            name={name}
                                            options={getGenders(t)}
                                            value={value || ""}
                                            onChange={onChange}
                                            disabled={!field.editable && !!value}
                                            required={field.required}
                                            label={t("userData_gender")}
                                        />
                                    )}
                                    control={control}
                                    name="gender"
                                />
                            </div>
                        );
                }
            }
            return null;
        };

        const renderAddressFields = (field: AccountDetailsCollectionConfig) => {
            if (field.enable) {
                switch (field.name) {
                    case SignupInputFieldName.Country:
                        return (
                            <div className={styles.field} key={field.name}>
                                <Controller
                                    render={({ field: { onChange, value, name } }) => (
                                        <UniversalDeviceDropdown
                                            name={name}
                                            options={getCountries(
                                                COMMON_SITE_CONFIGS.country.supported,
                                            )}
                                            value={value || ""}
                                            onChange={onChange}
                                            required={field.required}
                                            disabled={!field.editable && !!value}
                                            error={errors.country}
                                            label={t("userData_country")}
                                        />
                                    )}
                                    control={control}
                                    name="country"
                                />
                            </div>
                        );
                    case SignupInputFieldName.City:
                        return (
                            <div className={styles.field} key={field.name}>
                                <Controller
                                    render={({ field: { onChange, value, name } }) => (
                                        <Input
                                            label={t("userData_city")}
                                            name={name}
                                            onChange={onChange}
                                            value={value}
                                            error={errors.city}
                                            disabled={!field.editable && !!value}
                                            required={field.required}
                                        />
                                    )}
                                    control={control}
                                    name="city"
                                />
                            </div>
                        );
                    case SignupInputFieldName.State:
                        return (
                            <div className={styles.field} key={field.name}>
                                <Controller
                                    render={({ field: { onChange, value, name } }) => (
                                        <UniversalDeviceDropdown
                                            name={name}
                                            options={
                                                countryStatesLoading ? [] : getCountryStates(states)
                                            }
                                            value={value || ""}
                                            onChange={onChange}
                                            required={field.required}
                                            error={errors.state}
                                            disabled={
                                                (!field.editable && !!value) || countryStatesLoading
                                            }
                                            label={t("userData_state")}
                                        />
                                    )}
                                    control={control}
                                    name="state"
                                />
                            </div>
                        );
                    case SignupInputFieldName.Address:
                        return (
                            <div className={styles.field} key={field.name}>
                                <Controller
                                    render={({ field: { onChange, value, name } }) => (
                                        <Input
                                            label={t("userData_address")}
                                            name={name}
                                            onChange={onChange}
                                            value={value}
                                            error={errors.address}
                                            disabled={!field.editable && !!value}
                                            required={field.required}
                                        />
                                    )}
                                    control={control}
                                    name="address"
                                />
                            </div>
                        );
                    case SignupInputFieldName.ZipCode:
                        return (
                            <div className={styles.field} key={field.name}>
                                <Controller
                                    render={({ field: { onChange, value, name } }) => (
                                        <Input
                                            label={t("userData_zipCode")}
                                            name={name}
                                            onChange={onChange}
                                            value={value}
                                            error={errors.zip_code}
                                            disabled={!field.editable && !!value}
                                            required={field.required}
                                        />
                                    )}
                                    control={control}
                                    name="zip_code"
                                />
                            </div>
                        );
                }
            }
            return null;
        };

        switch (config?.name) {
            case AccountDetailsCollectionName.Phone: {
                const isPhoneInputEditable =
                    config.editable || (isPhoneVerificationRequired && !isPhoneVerified);

                return (
                    <div className={classNames(styles.field, styles.phone)}>
                        <Controller
                            render={({ field: { onChange, value } }) => (
                                <PhoneInput
                                    onChange={onChange}
                                    disabled={!isPhoneInputEditable && !!value}
                                    value={value}
                                    setError={setError}
                                    clearErrors={clearErrors}
                                    required={config.required}
                                    error={errors.phone || (errors.root?.apiError as FieldError)}
                                />
                            )}
                            control={control}
                            name="phone"
                        />
                        {isPhoneVerificationRequired &&
                            !isPhoneVerified &&
                            !showVerificationDialog && (
                                <span
                                    className={styles.verifyBtn}
                                    onClick={() => setShowVerificationDialog(true)}>
                                    <span>
                                        <Translate tid="verification_verify" />
                                    </span>
                                </span>
                            )}
                        {showVerificationDialog && (
                            <div className={styles.verification}>
                                <VerificationItem type="phone" title="" verified={isPhoneVerified}>
                                    <Translate
                                        tid="verification_phone_description"
                                        replace={{ phone: userData?.phone || "" }}
                                        dangerous>
                                        <div className={styles.text} />
                                    </Translate>
                                </VerificationItem>
                            </div>
                        )}
                    </div>
                );
            }
            case AccountDetailsCollectionName.Email: {
                const isEmailInputEditable =
                    config.editable || (isEmailVerificationRequired && !isEmailVerified);

                return (
                    <div className={classNames(styles.field, styles.email)}>
                        <Controller
                            render={({ field: { onChange, value, name } }) => {
                                return (
                                    <Input
                                        name={name}
                                        onChange={onChange}
                                        value={value}
                                        error={errors.email}
                                        disabled={!isEmailInputEditable && !!value}
                                        required={config.required}
                                    />
                                );
                            }}
                            control={control}
                            name="email"
                        />
                        {isEmailVerificationRequired &&
                            !isEmailVerified &&
                            !showVerificationDialog && (
                                <span
                                    className={styles.verifyBtn}
                                    onClick={() => setShowVerificationDialog(true)}>
                                    <span>
                                        <Translate tid="verification_verify" />
                                    </span>
                                </span>
                            )}
                        {showVerificationDialog && (
                            <div className={styles.verification}>
                                <VerificationItem type="email" title="" verified={isEmailVerified}>
                                    <Translate
                                        tid="verification_email_description"
                                        replace={{ email: userData?.email || "" }}
                                        dangerous>
                                        <div className={styles.text} />
                                    </Translate>
                                </VerificationItem>
                            </div>
                        )}
                    </div>
                );
            }
            case AccountDetailsCollectionName.Identity:
                return config.config?.map(renderIdentityFields);
            case AccountDetailsCollectionName.BirthDate:
                return (
                    <div className={styles.field}>
                        <Controller
                            render={({ field: { onChange, value } }) => {
                                const [year, month, day] = (value || "").split("-");
                                return (
                                    <DatePicker
                                        label=""
                                        value={
                                            new Date(
                                                parseInt(year),
                                                parseInt(month) - 1,
                                                parseInt(day),
                                            )
                                        }
                                        onChange={(val) =>
                                            onChange(formatDate(new Date(val), "yyyy-MM-dd", false))
                                        }
                                        disabled={!config.editable && !!value}
                                        wrapperClassname={styles.birthday}
                                        required={config.required}
                                    />
                                );
                            }}
                            control={control}
                            name="birth_date"
                        />
                    </div>
                );
            case AccountDetailsCollectionName.Address:
                return config.config?.map(renderAddressFields);
            case AccountDetailsCollectionName.BankDetails:
                return (
                    <>
                        <div className={styles.field}>
                            <Controller
                                render={({ field: { onChange, value, name } }) => {
                                    return (
                                        <Input
                                            label="Bank"
                                            name={name}
                                            onChange={onChange}
                                            value={value}
                                            error={errors.last_name}
                                            disabled={!config.editable && !!value}
                                        />
                                    );
                                }}
                                control={control}
                                name="last_name"
                            />
                        </div>
                        <div className={styles.field}>
                            <Controller
                                render={({ field: { onChange, value, name } }) => {
                                    return (
                                        <Input
                                            label="Agency"
                                            name={name}
                                            onChange={onChange}
                                            value={value}
                                            error={errors.last_name}
                                            disabled={!config.editable && !!value}
                                        />
                                    );
                                }}
                                control={control}
                                name="last_name"
                            />
                        </div>
                        <div className={styles.field}>
                            <Controller
                                render={({ field: { onChange, value, name } }) => {
                                    return (
                                        <Input
                                            label="Account"
                                            name={name}
                                            onChange={onChange}
                                            value={value}
                                            error={errors.last_name}
                                            disabled={!config.editable && !!value}
                                        />
                                    );
                                }}
                                control={control}
                                name="last_name"
                            />
                        </div>
                    </>
                );
            default:
                return null;
        }
    }, [
        config?.name,
        config.config,
        config.editable,
        config.required,
        control,
        t,
        errors.first_name,
        errors.last_name,
        errors.country,
        errors.city,
        errors.state,
        errors.address,
        errors.zip_code,
        errors.phone,
        errors.root?.apiError,
        errors.email,
        COMMON_SITE_CONFIGS.country.supported,
        countryStatesLoading,
        states,
        isPhoneVerificationRequired,
        isPhoneVerified,
        showVerificationDialog,
        userData?.phone,
        userData?.email,
        isEmailVerificationRequired,
        isEmailVerified,
        setError,
        clearErrors,
        formatDate,
    ]);

    const renderInfo = useMemo(() => {
        let content;
        switch (config?.name) {
            case AccountDetailsCollectionName.Phone:
                content = (
                    <Translate
                        tid="account_details_personal_contactsPanel_warning"
                        namespace={TranslationScopes.UserAccount}
                    />
                );
                break;
            default:
                return null;
        }

        return <p className={styles.message}>{content}</p>;
    }, [config?.name]);

    return (
        <>
            <AccountDetailsFormBlock
                testId={`update-details-form-${config.name}`}
                renderHeader={renderLabel}
                notification={notification}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className={styles.blockContent}>{renderForm}</div>
                    <div className={styles.blockFooter}>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleCancel}
                            disabled={!isDirty || loading}>
                            <span>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/close.svg`}
                                    className="svg-icon"
                                />
                                <Translate tid="base_cancel" />
                            </span>
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={
                                !isDirty ||
                                isSubmitting ||
                                !isValid ||
                                !!errors.root?.apiError ||
                                loading
                            }>
                            <span>
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-save.svg`}
                                    className="svg-icon"
                                />

                                <Translate tid="base_save" />
                            </span>
                        </Button>
                    </div>
                </form>
            </AccountDetailsFormBlock>
            {renderInfo}
        </>
    );
};

export default UserInfoForm;
